(ns hai.ui.components.collection.filters
  "Events and components for collection components, such as tables, that support filtering"
  (:require
   [hai.entity.helpers :as entity]
   [hai.ui.components :as c]
   [hai.ui.components.form.core :as form]
   [hai.ui.components.form.input :as input]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e defsub]]
   [humbleai.belt.malli.transform.translate-keys :as translate-keys]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(def remote-filter-types
  #{:filter.remote/select})

(def FilterTypes
  (into [:enum
         :filter/search
         :filter/select
         :filter/multi-select]
        remote-filter-types))

(def Filter
  [:map
   {:closed false}
   [:table.column.filter/schema
    {::translate-keys/from :schema}
    [:fn entity/->schema]]
   [:table.column.filter/type
    {::translate-keys/from :type}
    FilterTypes]
   [:table.column.filter/field
    {::translate-keys/from :field}
    ifn?]
   ;; Optional declarative pill config, consumed by `pill-bar`. A filter appears
   ;; in the pill bar iff it declares a `:label`. `:icon` is a component fn
   ;; (`[icon]`); `:summary` is `(value column) -> hiccup/string` — the closed-pill
   ;; body renderer, keyed off the live filter value. All three flow through the
   ;; standard column `:filter` config, so the pill bar stays drop-in.
   [:table.column.filter/label
    {::translate-keys/from :label :optional true}
    :string]
   [:table.column.filter/icon
    {::translate-keys/from :icon :optional true}
    ifn?]
   [:table.column.filter/summary
    {::translate-keys/from :summary :optional true}
    ifn?]])

(defn has-filters?
  [{:table/keys [columns]}]
  (some :table.column/filter columns))

(defn needs-filter-values?
  [{:table/keys [columns]}]
  (some (fn [{{:table.column.filter/keys [type]} :table.column/filter}]
          (contains? remote-filter-types type))
        columns))

(defn query-for-filters?
  [table]
  (needs-filter-values? table))

(defn collate-filter-schema
  [{:table/keys [columns]}]
  (into []
        (comp (map (fn [{:table.column/keys [attr filter]}]
                     (when-let [{:table.column.filter/keys [schema] :keys [default]} filter]
                       ;; All filters can be nil which represents no filter set
                       ;; NOTE `false` is a valid filter value
                       [attr
                        {:default default}
                        [:or :nil schema]])))
              (remove nil?))
        columns))

(defsub ::values
  [?table
   table <- [:table ?table]]
  (get table :table/filter-values))

(defsub ::columns
  [?table
   values <- [::values ?table]
   columns <- [:table/columns ?table]]
  (into []
        (map (fn [{:table.column/keys [filter attr] :as column}]
               (if-let [{:table.column.filter/keys [type]} filter]
                 (assoc column :table.column/filter
                        (case type
                          :filter.remote/select (assoc filter :form.field.select/options (get values attr))
                          filter))
                 column)))
        columns))

(o/defstyled filter-icon* :div
  :flex :items-center :justify-center
  :h-100%
  [:svg :w-20px :h-20px {:fill "var(--gray-10)"}]
  ([]
   [icons/filled-filter_alt]))

(defn filter-icon
  []
  [c/TableColumnHeaderCell
   {:style {:padding-right "0"}}
   [filter-icon*]])

(defn column-field
  "Renders a single filter column's widget — the `:field` component wired to that
   column's live form-field state (`current-value`, `set-value!`, …) merged with its
   `:filter` config. Returns nil for columns without a filter.

   `table` must carry `:table/form` (the filters form), as it does inside a
   `:form/render`. This is the seam a custom `:filters/render` reuses so it never has
   to re-derive field props — see `process-schema`."
  [{{:form/keys [fields]} :table/form} {:table.column/keys [filter attr]}]
  (when-let [{:table.column.filter/keys [field]} filter]
    [field (-> filter
               (merge (get fields attr))
               (form/decode-field-props))]))

(defn row
  [{:as table
    :table/keys [bulk-actions]}]
  (r/with-let [columns (<s [::columns table])]
    (cond-> [c/TableRow]
      bulk-actions (conj [filter-icon])
      :always (into (map (fn [column]
                           [c/TableColumnHeaderCell
                            [column-field table column]]))
                    @columns))))

(defn process-schema
  "Wires the `:table/filters` sub-form into the collection form.

   Filter UI can be fully replaced via a `:filters/render` collection prop (mirrors
   `:settings/render`). It's a component `[table]` where `table` carries `:table/form`
   (the filters form); read the filter columns with `@(<s [::columns table])` and
   render each with `[column-field table column]` (or drive `set-value!` directly).
   Defaults to the built-in `row`. The value/query plumbing is unchanged either way."
  [schema table]
  (let [render (or (:filters/render table) row)]
    (cond-> schema
      (has-filters? table)
      (conj [:table/filters
             {:form.field/on-change (fn [_]
                                      (>e [:table/reset-cursors table])
                                      (>e [:table/debounce-resolve table]))}
             (into [:map {:form/render (fn [form]
                                         [render (assoc table :table/form form)])
                          ;; NOTE this ensures the filter default values are applied
                          :default {}}]
                   (collate-filter-schema table))]))))

(defn component
  [collection]
  (when-let [filters (-> collection :table/form :form/fields :table/filters)]
    [form/component filters]))

(o/defstyled cards-row :div
  :flex :flex-wrap :items-center
  {:gap "12px" :padding "8px 0" :row-gap "8px"}
  ;; Inputs default to filling their container; in a `<td>` that's bounded,
  ;; but here the flex parent has no width cap so they stretch full-width.
  ;; Cap each filter at a sane intrinsic width and let them wrap.
  [:>* {:flex "0 1 220px" :min-width "0"}])

(defn cards-row-render
  "Flex-row equivalent of `row`. Same field instantiation; just swaps the
   wrapping element so the cards view doesn't need a <table> around it."
  [{:as table {:form/keys [fields]} :table/form}]
  (r/with-let [columns (<s [::columns table])]
    (into [cards-row]
          (keep (fn [{:table.column/keys [filter attr]}]
                  (when-let [{:table.column.filter/keys [field]} filter]
                    [field (-> filter
                               (merge (get fields attr))
                               (form/decode-field-props))])))
          @columns)))

(defn cards-component
  "Filter renderer for the cards view. Reuses `form/component` (so fields
   get the form-setup wiring), but overrides `:form/render` to wrap the
   row in a flex div instead of a `<tr>`.

   A `:filters/render` escape hatch takes precedence: `process-schema` already
   installed the custom renderer as the field's `:form/render`, so we leave it
   alone (clobbering it here is what previously made `:filters/render` a no-op in
   the cards view)."
  [collection]
  (when-let [filters (-> collection :table/form :form/fields :table/filters)]
    [form/component
     (cond-> filters
       (not (:filters/render collection))
       (assoc :form/render
              (fn [form] [cards-row-render (assoc collection :table/form form)])))]))

;; Filter helpers

(defn text-search
  [options]
  (merge {:schema :string
          :type :filter/search
          :placeholder "Search..."
          :field input/field}
         options))
